import { WizardProfileData } from '@/mocks/wizard';
import { MockUser } from '@/mocks/users';

interface StepProfileProps {
  data: WizardProfileData;
  user: MockUser;
  onChange: (data: WizardProfileData) => void;
}

const TIMEZONES = [
  'Europe/Kiev',
  'Europe/Warsaw',
  'Europe/Berlin',
  'Europe/London',
  'UTC',
];

const LANGUAGES = [
  { value: 'uk', label: 'Українська' },
  { value: 'en', label: 'English' },
  { value: 'pl', label: 'Polski' },
];

const inputCls = 'w-full px-3.5 py-2.5 rounded-lg text-sm border transition-all outline-none focus:border-gray-400';
const inputStyle = { background: '#FAFAFA', borderColor: '#E5E5E8', color: '#1A1A1A', fontFamily: 'Inter, sans-serif' };

export default function StepProfile({ data, user, onChange }: StepProfileProps) {
  const set = (field: keyof WizardProfileData, value: string) =>
    onChange({ ...data, [field]: value });

  const initials = user.avatar || (user.displayName.split(' ').map((p) => p[0]).join('').toUpperCase());

  return (
    <div className="max-w-2xl mx-auto w-full">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#1A1A1A', letterSpacing: '-0.02em' }}>
          Заповніть свій профіль
        </h2>
        <p className="text-sm" style={{ color: '#8B8B94' }}>
          Ці дані будуть відображатися в системі та у сповіщеннях
        </p>
      </div>

      {/* Avatar block */}
      <div className="flex items-center gap-4 mb-8 p-4 rounded-xl" style={{ background: '#F7F7F9', border: '1px solid #E5E5E8' }}>
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0"
          style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold mb-0.5" style={{ color: '#1A1A1A' }}>{user.displayName}</p>
          <p className="text-xs mb-2" style={{ color: '#8B8B94' }}>{user.email}</p>
          <span
            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ background: 'color-mix(in srgb, var(--err) 10%, transparent)', color: 'var(--accent)', border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)' }}
          >
            <i className="ri-user-star-line text-[10px]" />
            {user.role}
          </span>
        </div>
        <button
          type="button"
          className="px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
          style={{ background: '#fff', border: '1px solid #E5E5E8', color: '#1A1A1A' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#F0F0F2'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
        >
          <i className="ri-upload-line mr-1" />
          Змінити фото
        </button>
      </div>

      {/* Fields */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1A1A1A' }}>Ім&apos;я *</label>
          <input
            type="text"
            value={data.firstName}
            onChange={(e) => set('firstName', e.target.value)}
            placeholder="Артем"
            className={inputCls}
            style={inputStyle}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1A1A1A' }}>Прізвище *</label>
          <input
            type="text"
            value={data.lastName}
            onChange={(e) => set('lastName', e.target.value)}
            placeholder="Коваль"
            className={inputCls}
            style={inputStyle}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1A1A1A' }}>Посада *</label>
          <input
            type="text"
            value={data.position}
            onChange={(e) => set('position', e.target.value)}
            placeholder="CEO / Директор"
            className={inputCls}
            style={inputStyle}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1A1A1A' }}>Телефон</label>
          <input
            type="tel"
            value={data.phone}
            onChange={(e) => set('phone', e.target.value)}
            placeholder="+380 XX XXX XX XX"
            className={inputCls}
            style={inputStyle}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1A1A1A' }}>Часовий пояс</label>
          <select
            value={data.timezone}
            onChange={(e) => set('timezone', e.target.value)}
            className={inputCls}
            style={inputStyle}
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1A1A1A' }}>Мова інтерфейсу</label>
          <select
            value={data.language}
            onChange={(e) => set('language', e.target.value)}
            className={inputCls}
            style={inputStyle}
          >
            {LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
